import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { apiClient } from 'Utils/api';
import { useCountry } from 'Context/CountryContext';

interface CartItem {
  id: string;
  brand: string;
  model: string;
  slug: string;
  priceEur: number;
  priceCents: number;
  reservedUntil: string;
  image: string | null;
  referenceNumber?: string;
  taxScheme?: 'MARGIN_25A_USTG' | 'STANDARD_VAT_19';
  isExport?: boolean;
  appliedTaxPermille?: number | null;
}

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  total: number;
  loading: boolean;
  addToCart: (watchId: string) => Promise<{ success: boolean; message: string }>;
  removeFromCart: (watchId: string) => Promise<void>;
  refreshCart: () => Promise<void>;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

const getSessionId = () => {
  let sessionId = localStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { country } = useCountry();

  const refreshCart = useCallback(async () => {
    try {
      const sessionId = getSessionId();
      const response = await apiClient.get(`/api/cart`, {
        params: { sessionId },
      });

      if (response.data.status === 'success') {
        setItems(response.data.items || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }, []);

  useEffect(() => {
    refreshCart();

    // Refresh cart every 30 seconds to check for expired items
    const interval = setInterval(refreshCart, 30000);

    return () => clearInterval(interval);
  }, [refreshCart]);

  // Re-fetch when the delivery country changes so prices (export/VAT) update.
  useEffect(() => {
    refreshCart();
  }, [country, refreshCart]);

  const addToCart = async (watchId: string): Promise<{ success: boolean; message: string }> => {
    setLoading(true);
    try {
      const sessionId = getSessionId();
      const response = await apiClient.post(`/api/cart/add`, {
        watchId,
        sessionId,
      });

      if (response.data.status === 'success') {
        await refreshCart();
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message || 'Fehler beim Hinzufügen' };
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Fehler beim Hinzufügen zum Warenkorb';
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (watchId: string) => {
    try {
      const sessionId = getSessionId();
      await apiClient.delete(`/api/cart/${watchId}`, { params: { sessionId } });
      await refreshCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  const clearCart = () => {
    setItems([]);
    setTotal(0);
  };

  const value: CartContextType = {
    items,
    itemCount: items.length,
    total,
    loading,
    addToCart,
    removeFromCart,
    refreshCart,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
